import React, { useState } from "react";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLShopTensionPresetCard — RACKET-LAB-ROLE-2 (Feb 2026).
 *
 * Inline admin-only block to view / edit the per-shop default
 * stringing-tension preset (`default_tension_unit`,
 * `default_main_tension`, `default_cross_tension` inside
 * `racket_lab_shops.settings`).
 *
 * Behaviour:
 *   - admin / staff (workforce write strict gate) can edit & save.
 *   - shop_manager / operator / qa_manager / qa_tester see READ-ONLY
 *     (the PATCH endpoint will 403 anyway; we hide the form for them).
 *   - Empty state when all three fields are null.
 *   - Saving re-PATCHes `settings` with the full ShopSettings payload
 *     so unrelated keys (intake_modes, language, service_mode, ...)
 *     are preserved verbatim.
 *
 * No customer-facing surface. No real notifications. No migrations.
 */
const TENSION_UNITS = ["kg", "lbs"];

const RLShopTensionPresetCard = ({ shop, onSaved }) => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const { run, loading } = useAdminMutation();
  const canEdit = ctx?.profile === "admin" || ctx?.profile === "staff";
  const settings = shop?.settings || {};

  const [unit, setUnit] = useState(settings.default_tension_unit || "");
  const [main, setMain] = useState(
    settings.default_main_tension == null ? "" : String(settings.default_main_tension)
  );
  const [cross, setCross] = useState(
    settings.default_cross_tension == null ? "" : String(settings.default_cross_tension)
  );

  const hasPreset =
    settings.default_tension_unit != null ||
    settings.default_main_tension != null ||
    settings.default_cross_tension != null;

  const onSave = async () => {
    const next = {
      ...settings,
      default_tension_unit: unit || null,
      default_main_tension: main === "" ? null : parseInt(main, 10),
      default_cross_tension: cross === "" ? null : parseInt(cross, 10),
    };
    const updated = await run("patch", `/admin/racket-lab/shops/${shop.id}`, {
      settings: next,
    });
    if (updated) onSaved?.(updated);
  };

  const onClear = async () => {
    const next = {
      ...settings,
      default_tension_unit: null,
      default_main_tension: null,
      default_cross_tension: null,
    };
    const updated = await run("patch", `/admin/racket-lab/shops/${shop.id}`, {
      settings: next,
    });
    if (updated) onSaved?.(updated);
  };

  return (
    <section
      data-testid="rl-shop-tension-preset-card"
      className="rounded-md border border-slate-200 bg-white p-4 md:col-span-2"
    >
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          {t("shops.detail.tension_preset.title")}
        </h3>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">
          {t("shops.detail.tension_preset.admin_only")}
        </span>
      </header>
      <p className="mb-3 text-[11px] text-slate-500">
        {t("shops.detail.tension_preset.hint")}
      </p>

      {!hasPreset && (
        <p
          data-testid="rl-shop-tension-preset-empty"
          className="mb-3 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
        >
          {t("shops.detail.tension_preset.empty")}
        </p>
      )}

      {!canEdit && hasPreset && (
        <ul
          data-testid="rl-shop-tension-preset-readonly"
          className="grid grid-cols-3 gap-3 text-xs"
        >
          <li>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.unit")}
            </p>
            <p className="font-semibold">{settings.default_tension_unit ?? "—"}</p>
          </li>
          <li>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.main")}
            </p>
            <p className="font-semibold">{settings.default_main_tension ?? "—"}</p>
          </li>
          <li>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.cross")}
            </p>
            <p className="font-semibold">{settings.default_cross_tension ?? "—"}</p>
          </li>
        </ul>
      )}

      {canEdit && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.unit")}
            </span>
            <select
              data-testid="rl-shop-tension-preset-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">—</option>
              {TENSION_UNITS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.main")}
            </span>
            <input
              data-testid="rl-shop-tension-preset-main"
              type="number"
              min={1}
              max={80}
              value={main}
              onChange={(e) => setMain(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="—"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">
              {t("shops.detail.tension_preset.cross")}
            </span>
            <input
              data-testid="rl-shop-tension-preset-cross"
              type="number"
              min={1}
              max={80}
              value={cross}
              onChange={(e) => setCross(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="—"
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              data-testid="rl-shop-tension-preset-save"
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {loading ? t("shops.detail.tension_preset.saving") : t("shops.detail.tension_preset.save")}
            </button>
            {hasPreset && (
              <button
                type="button"
                data-testid="rl-shop-tension-preset-clear"
                onClick={onClear}
                disabled={loading}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed"
              >
                {t("shops.detail.tension_preset.clear")}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default RLShopTensionPresetCard;
